<script setup lang="ts">
import { debounce } from "~/utils/Misc"

defineProps<{
  theme: "light" | "dark"
}>()

const page = usePage<{
  search_results?: PublicKBArticle[]
}>()

// References
const searchField = ref<HTMLInputElement | null>(null)
const resultList = ref<HTMLElement | null>(null)

// State
const searchDone = ref(false)
const focused = ref(false)
const searchQuery = ref("")
const selectedResultIndex = ref(0)
const results = ref<PublicKBArticle[]>([])

watch(
  () => page.props.search_results,
  (searchResults) => {
    searchDone.value = true
    if (searchResults) {
      results.value = searchResults
    }
  },
)

// Handle keyup events
const onKeyUp = (event: KeyboardEvent) => {
  switch (event.key) {
    case "Enter":
      if (selectedResultIndex.value >= 0 && selectedResultIndex.value < results.value.length) {
        const result = results.value[selectedResultIndex.value]
        navigateToResult(result)
      }
      break

    case "ArrowUp":
      selectedResultIndex.value = Math.max(0, selectedResultIndex.value - 1)
      break

    case "ArrowDown":
      selectedResultIndex.value = Math.min(results.value.length - 1, selectedResultIndex.value + 1)
      break

    default:
      searchQuery.value = searchField.value?.value || ""
      break
  }
}

// Prevent cursor movement on arrow keys
const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === "ArrowUp" || event.key === "ArrowDown") {
    event.preventDefault()
  }
}

// Handle mouse hover on results
const onMouseOver = (index: number) => {
  selectedResultIndex.value = index
}

// Perform search
const performSearch = () => {
  selectedResultIndex.value = 0

  router.reload({
    only: ["search_results"],
    data: {
      search_query: searchQuery.value,
    },
    preserveUrl: true,
  })
}

// Navigate to result
const navigateToResult = (article: PublicKBArticle) => {
  router.visit(article.path, {
    preserveState: true,
  })
}

// Watch for search query changes
const debouncedPerformSearch = debounce(performSearch, 200)

watch(searchQuery, () => {
  searchDone.value = false
  debouncedPerformSearch()
})
</script>

<template>
  <div class="relative">
    <!-- Search Field -->
    <div class="group flex items-center">
      <HUIIcon
        icon="search"
        variant="solid"
        class="pointer-events-none absolute left-0 p-4 text-xl group-has-focus:text-gray-800"
        :class="theme === 'light' ? 'text-white' : 'text-gray-800'"
      />
      <input
        ref="searchField"
        class="w-full rounded-xl border-0 bg-white/50 py-3 pr-2 pl-12 text-xl shadow-none transition outline-none focus:bg-white focus:text-gray-800 focus:placeholder-gray-800 focus:ring-0 sm:py-4"
        :class="theme === 'light' ? 'text-white placeholder-white' : 'text-gray-800 placeholder-gray-800'"
        type="text"
        :placeholder="$t('.placeholder')"
        role="combobox"
        aria-controls="options"
        aria-expanded="false"
        v-model="searchQuery"
        @focus="focused = true"
        @blur="focused = false"
        @keyup="onKeyUp"
        @keydown="onKeyDown"
      />
      <HUIIcon
        v-if="searchQuery"
        icon="xmark"
        variant="solid"
        @click="searchQuery = ''"
        class="absolute right-0 cursor-pointer p-4 text-xl text-gray-600"
      />
    </div>

    <!-- Results -->
    <div
      v-if="focused && searchQuery && searchDone"
      class="absolute top-18 z-20 max-h-[min(70vh,800px)] w-full overflow-y-scroll rounded-lg bg-white p-4 shadow-2xl ring ring-gray-300 transition-all"
      style=""
    >
      <div ref="resultList" class="flex w-full flex-col gap-y-3 overflow-auto" v-if="results.length > 0">
        <div v-for="(result, index) in results" :key="index">
          <a
            href="javascript:;"
            class="flex flex-col rounded-lg border border-gray-200 px-4 py-3 hover:border-gray-400"
            :class="{ 'border-gray-400 bg-gray-100': index === selectedResultIndex }"
            @mouseover="onMouseOver(index)"
            @mousedown="navigateToResult(result)"
          >
            <div class="text-lg font-medium text-gray-900">{{ result.title }}</div>
            <div class="text-gray-500" v-html="result.search_highlight"></div>
          </a>
        </div>
      </div>
      <div class="py-16 text-center sm:px-6" v-if="results.length === 0">
        <HUIIcon icon="search" variant="solid" class="mx-auto text-2xl text-gray-400" />
        <p class="mt-2 font-normal text-gray-500">
          {{ $t(".no_results", { query: searchQuery }) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
::v-deep(mark) {
  background-color: var(--color-blue-100);
}
</style>

<i18n lang="yaml">
de:
  placeholder: Suchen…
  no_results: Keine Ergebnisse für "%{query}" gefunden
  enter_query: Geben Sie einen Suchbegriff ein…
</i18n>
